import { gatewayClient, createService } from './connectors/httpClient';
import ProxyServerConnector from './connectors/ProxyServerConnector';

const transcribesHttpClient = gatewayClient('/transcribes');

function buildTranscribeStatusStreamPath(transcribeJobId: number): string {
    const gatewayPrefix = ProxyServerConnector.gatewayPrefix || '/gateway';
    const gatewayUrl = new URL(gatewayPrefix, window.location.origin);

    if (gatewayUrl.origin !== window.location.origin) {
        throw new Error('INVALID_GATEWAY_PREFIX');
    }

    const gatewayPath = gatewayUrl.pathname.replace(/\/$/, '');

    return `${gatewayPath}/transcribes/${transcribeJobId}/status/stream`;
}

export type TranscribeStatus = 'COMPLETED' | 'RENDER_FAILED' | 'TRANSCRIPTION_FAILED';

const TERMINAL_STATUSES: readonly TranscribeStatus[] = ['COMPLETED', 'RENDER_FAILED', 'TRANSCRIPTION_FAILED'];

const SSE_EVENT_SEPARATOR = /\r\n\r\n|\n\n|\r\r/;
const SSE_LINE_SEPARATOR = /\r\n|\n|\r/;

function splitSseEvents(): TransformStream<string, string> {
    let buffer = '';

    return new TransformStream({
        transform(chunk, controller) {
            buffer += chunk;

            const events = buffer.split(SSE_EVENT_SEPARATOR);

            buffer = events.pop() ?? '';
            events.forEach((event) => controller.enqueue(event));
        },
        flush(controller) {
            if (buffer.trim()) {
                controller.enqueue(buffer);
            }
        },
    });
}

function parseStatusEvent(event: string): TranscribeStatus | null {
    let eventType = '';
    const dataLines: string[] = [];

    event.split(SSE_LINE_SEPARATOR).forEach((line) => {
        if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).replace(/^ /, ''));
        }
    });

    if (eventType && eventType !== 'status') {
        return null;
    }

    const dataText = dataLines.join('\n');

    if (!dataText) {
        return null;
    }

    try {
        const data = JSON.parse(dataText);

        return data?.status ?? data;
    } catch {
        return null;
    }
}

const transcribeService = {
    ...createService(transcribesHttpClient, (client) => ({
        createTranscribeJob(data: { elements: unknown[]; duration: number; languageCode?: string }) {
            return client.post<{ id: number; status: 'PENDING' | 'RENDERED' | 'FAILED'; message?: string }>(
                `/spec`,
                data,
            );
        },

        createVariationTranscribeJob(data: { variationId: string; languageCode?: string }) {
            return client.post<{ id: number; status: 'PENDING' | 'RENDERED' | 'FAILED'; message?: string }>(
                `/variation`,
                data,
            );
        },

        getTranscribeUrl(transcribeJobId: number) {
            return client.get<{ signedUrl?: string; expiresAt?: number }>(`/${transcribeJobId}/url`);
        },
    })),

    listenToTranscribeStatus(
        transcribeJobId: number,
        onStatus: (status: TranscribeStatus) => void,
        onError: (error: Error) => void,
    ): () => void {
        const streamPath = buildTranscribeStatusStreamPath(transcribeJobId);
        const abortController = new AbortController();
        let reachedTerminalStatus = false;

        const token = ProxyServerConnector.getToken();
        const headers: Record<string, string> = {
            Accept: 'text/event-stream',
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        // EventSource cannot send an Authorization header, so the SSE stream is read over fetch.
        fetch(streamPath, {
            method: 'GET',
            headers,
            credentials: 'same-origin',
            signal: abortController.signal,
        })
            .then((response) => {
                if (!response.ok || !response.body) {
                    throw new Error(`SSE_REQUEST_FAILED_${response.status}`);
                }

                return response.body
                    .pipeThrough(new TextDecoderStream())
                    .pipeThrough(splitSseEvents())
                    .pipeTo(
                        new WritableStream({
                            write(event) {
                                if (reachedTerminalStatus) {
                                    return;
                                }

                                const status = parseStatusEvent(event);

                                if (!status) {
                                    return;
                                }

                                onStatus(status);

                                if (TERMINAL_STATUSES.includes(status)) {
                                    reachedTerminalStatus = true;
                                    abortController.abort();
                                }
                            },
                        }),
                        { signal: abortController.signal },
                    );
            })
            .then(() => {
                if (!reachedTerminalStatus) {
                    throw new Error('SSE_STREAM_CLOSED');
                }
            })
            .catch((error) => {
                if (reachedTerminalStatus || abortController.signal.aborted) {
                    return;
                }

                onError(error instanceof Error ? error : new Error(String(error)));
            });

        return () => abortController.abort();
    },
};

export default transcribeService;
